import { Modal, Typography, Box, Button, Checkbox } from '@mui/material';
import { ModalTermsConditions } from './ModalTermsConditions';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, RootState } from 'shared/store/store';
import styles from './styles/ModalSignatureUpload.module.scss';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';
import { CustomFileUploader } from '../CustomFileUploader';
import { useState, useEffect } from 'react';
import { postAuditorSignature, getAuditorSignature } from 'shared/api/services';
import { AuditorSignatureResponse } from 'shared/models';

export const ModalSignatureUpload = () => {
  const dispatch = useDispatch<Dispatch>();
  const { showSignatureModal, signatureFile } = useSelector((state: RootState) => state.userMenu);
  const [file, setFile] = useState<File | null>(signatureFile);
  const [currentSignature, setCurrentSignature] = useState<AuditorSignatureResponse | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isReadOnlyTerms, setIsReadOnlyTerms] = useState(false);

  useEffect(() => {
    const fetchCurrentSignature = async () => {
      if (showSignatureModal) {
        setIsLoading(true);
        const signature = await getAuditorSignature();
        setCurrentSignature(signature);
        if (signature?.termsAccepted) {
          setAcceptedTerms(true);
        }
        setIsLoading(false);
      }
    };
    fetchCurrentSignature();
  }, [showSignatureModal]);

  const handleClose = () => {
    dispatch.userMenu.setShowSignatureModal(false);
    dispatch.userMenu.setSignatureFile(null);
    setFile(null);
    setAcceptedTerms(false);
  };

  const handleFileChange = (selectedFile: File) => {
    setFile(selectedFile);
    dispatch.userMenu.setSignatureFile(selectedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    dispatch.userMenu.setSignatureFile(null);
  };

  const handleSave = async () => {
    if (!file) return;

    const response = await postAuditorSignature(file, acceptedTerms);
    if (response) {
      setCurrentSignature(response);
      handleClose();
    }
  };

  const handleCheckboxClick = () => {
    if (!acceptedTerms) {
      setShowTermsModal(true);
    }
  };

  const handleAcceptTerms = () => {
    setAcceptedTerms(true);
    setShowTermsModal(false);
  };

  const handleCancelTerms = () => {
    setShowTermsModal(false);
    setIsReadOnlyTerms(false);
  };

  const handleViewTerms = () => {
    setIsReadOnlyTerms(true);
    setShowTermsModal(true);
  };

  return (
    <>
      <Modal
        open={showSignatureModal}
        onClose={handleClose}
        className={styles.modalSignature}
        data-testid='modal-signature-upload'
      >
        <Box className={styles.container} data-testid='container-box'>
          <section className={styles.header}>
            <Typography className={styles.titleModal}>
              Cargar imagen firma digital
            </Typography>
            <IconButton
              className={styles.buttonClose}
              onClick={handleClose}
              data-testid='button-close'
            >
              <CloseIcon />
            </IconButton>
          </section>
          {currentSignature && currentSignature.hasSignature && (
            <section className={styles.currentSignatureInfo}>
              <Typography className={styles.infoTitle}>
                Ya has cargado una firma digital y Aceptados los terminos y condiciones
              </Typography>

              <Box className={styles.signatureActions}>
                <Button
                  className={styles.downloadButton}
                  onClick={() => window.open(currentSignature.downloadLink, '_blank')}
                  startIcon={<DownloadIcon sx={{ fontSize: '1rem' }} />}
                >
                  Descargar firma actual
                </Button>
              </Box>

              <Box className={styles.termsContainer}>
                <Box className={styles.termsCheckbox}>
                  <Checkbox
                    checked={currentSignature.termsAccepted}
                    disabled={true}
                    sx={{
                      color: currentSignature.termsAccepted ? '#2e7d32' : undefined,
                      '&.Mui-checked': {
                        color: '#2e7d32',
                      },
                    }}
                  />
                  {currentSignature.termsAccepted && (
                    <Typography className={styles.checkboxLabel} sx={{ fontSize: '0.875rem' }}>
                      Aceptados Términos y Condiciones del Acuerdo de Firma Electrónica
                    </Typography>
                  )}
                </Box>
                <Button
                  className={styles.viewTermsButton}
                  onClick={handleViewTerms}
                >
                  Ver Términos y Condiciones
                </Button>
              </Box>
            </section>
          )}
          {!isLoading && (
            <>
              {!currentSignature?.termsAccepted && (
                <section className={styles.checkboxSection}>
                  <Box className={styles.termsCheckbox}>
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={handleCheckboxClick}
                      color="primary"
                    />
                    <Typography className={styles.checkboxLabel}>
                      He leído y acepto los Términos y Condiciones del Acuerdo de Firma Electrónica
                    </Typography>
                  </Box>
                </section>
              )}
              <section className={styles.description}>
                <Typography className={styles.textDescription}>
                  Por favor seleccione una imagen en formato PNG para su firma digital
                </Typography>
                <Box className={styles.fileUploaderContainer}>
                  <CustomFileUploader
                    typeAccept={['PNG']}
                    fileLength={5}
                    filesAllowed={1}
                    multiple={false}
                    originalName={file?.name || null}
                    document={file || null}
                    handleChangeFile={handleFileChange}
                    removeFile={handleRemoveFile}
                    fullWidth={true}
                    isDisabled={!acceptedTerms}
                  />
                </Box>
              </section>
            </>
          )}
          <section className={styles.footerButton}>
            <Button
              className={styles.buttonCancel}
              onClick={handleClose}
              data-testid='button-cancel'
            >
              Cancelar
            </Button>
            <Button
              className={styles.buttonOk}
              onClick={handleSave}
              disabled={!file || !acceptedTerms}
              data-testid='button-save'
            >
              Guardar
            </Button>
          </section>
        </Box>
      </Modal>
      <ModalTermsConditions
        open={showTermsModal}
        onClose={handleCancelTerms}
        onAccept={handleAcceptTerms}
        readOnly={isReadOnlyTerms}
      />
    </>
  );
};
